import { useState } from 'react';
import type { EditorPage } from '@/mocks/editorLayout';
import { mockEditorPages } from '@/mocks/editorLayout';

interface PreviewModalProps {
  currentPage: EditorPage;
  onClose: () => void;
  onExport: () => void;
}

export default function PreviewModal({ currentPage, onClose, onExport }: PreviewModalProps) {
  const [previewPage, setPreviewPage] = useState(currentPage.id);
  const [viewMode, setViewMode] = useState<'single' | 'spread'>('single');

  const page = mockEditorPages.find((p) => p.id === previewPage) || currentPage;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
      {/* Preview Toolbar */}
      <div className="h-12 bg-[#111111] flex items-center px-4 gap-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-sans cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line text-sm"></i>
          Voltar ao Editor
        </button>

        <div className="flex-1"></div>

        <div className="flex items-center gap-1 bg-white/10 p-0.5">
          <button
            onClick={() => setViewMode('single')}
            className={`px-3 py-1 text-xs font-sans cursor-pointer transition-colors ${viewMode === 'single' ? 'bg-white text-[#111111] font-bold' : 'text-white/70 hover:text-white'}`}
          >
            Página
          </button>
          <button
            onClick={() => setViewMode('spread')}
            className={`px-3 py-1 text-xs font-sans cursor-pointer transition-colors ${viewMode === 'spread' ? 'bg-white text-[#111111] font-bold' : 'text-white/70 hover:text-white'}`}
          >
            Spread
          </button>
        </div>

        <div className="flex-1"></div>

        <button
          onClick={onExport}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-[#C41230] text-white text-xs font-sans font-bold cursor-pointer hover:bg-[#a00e26] transition-colors"
        >
          <i className="ri-file-pdf-line text-sm"></i>
          Exportar PDF
        </button>
      </div>

      {/* Preview Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Page Thumbnails */}
        <div className="w-24 bg-[#1a1a1a] overflow-y-auto py-3 flex flex-col items-center gap-3">
          {mockEditorPages.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreviewPage(p.id)}
              className={`w-16 cursor-pointer transition-all ${previewPage === p.id ? 'ring-2 ring-[#C41230]' : 'opacity-60 hover:opacity-100'}`}
            >
              <div className="w-full aspect-[3/4] bg-white flex flex-col p-1 gap-0.5">
                <div className="h-1.5 bg-[#111111] w-full"></div>
                <div className="flex gap-0.5 flex-1">
                  <div className="flex-1 bg-[#E8E8E8]"></div>
                  <div className="w-1/3 bg-[#F0F0F0]"></div>
                </div>
                <div className="h-2 bg-[#F0F0F0]"></div>
              </div>
              <p className="text-[8px] text-white/60 font-sans text-center mt-1">Pág. {p.number}</p>
            </button>
          ))}
        </div>

        {/* Main Preview */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-8 bg-[#2a2a2a]">
          <div className="bg-white shadow-2xl" style={{ width: 595, minHeight: 842 }}>
            {/* Page Header */}
            <div className="border-b-4 border-[#111111] px-6 py-3 flex items-center justify-between">
              <div>
                <p className="font-serif font-black text-[#111111] text-xl tracking-tight">JARDEL CITY POST</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex-1 h-px bg-[#111111]"></div>
                  <p className="text-[8px] font-sans text-[#666666] uppercase tracking-widest px-1">Edição #14820 · 8 de Abril de 2026</p>
                  <div className="flex-1 h-px bg-[#111111]"></div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[8px] font-sans text-[#999999] uppercase tracking-wide">Pág. {page.number}</p>
                <p className="text-[8px] font-sans text-[#C41230] font-bold uppercase">{page.section}</p>
              </div>
            </div>

            {/* Page Content Preview */}
            <div className="p-4">
              {page.blocks.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <p className="text-sm text-[#CCCCCC] font-sans">Página sem conteúdo</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {page.blocks.slice(0, 6).map((block) => (
                    <div key={block.id} className={`border border-[#E0E0E0] overflow-hidden ${
                      block.type === 'title' ? 'bg-[#111111]' :
                      block.type === 'ad' ? 'bg-[#F8F8F8]' : 'bg-white'
                    }`}>
                      {block.type === 'title' && (
                        <div className="px-3 py-2">
                          <p className="text-white font-serif font-bold text-sm uppercase tracking-widest">{block.content.title}</p>
                        </div>
                      )}
                      {block.type === 'article' && (
                        <div className="flex gap-3 p-3">
                          {block.content.imageUrl && (
                            <div className="w-20 h-16 flex-shrink-0 overflow-hidden">
                              <img src={block.content.imageUrl} alt="" className="w-full h-full object-cover object-top" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            {block.content.section && (
                              <p className="text-[8px] font-bold text-[#C41230] font-sans uppercase tracking-wide mb-0.5">{block.content.section}</p>
                            )}
                            <p className="text-xs font-bold text-[#111111] font-serif leading-tight line-clamp-2">{block.content.title}</p>
                            {block.content.body && (
                              <p className="text-[9px] text-[#555555] font-sans mt-1 line-clamp-2">{block.content.body}</p>
                            )}
                            {block.content.author && (
                              <p className="text-[8px] text-[#999999] font-sans mt-1">{block.content.author}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {block.type === 'image' && (
                        <div className="relative">
                          {block.content.imageUrl && (
                            <img src={block.content.imageUrl} alt="" className="w-full h-32 object-cover object-top" />
                          )}
                          {block.content.caption && (
                            <p className="text-[8px] text-[#666666] font-sans px-2 py-1 bg-[#F8F8F8] border-t border-[#E0E0E0]">{block.content.caption}</p>
                          )}
                        </div>
                      )}
                      {block.type === 'ad' && (
                        <div className="p-2 flex items-center gap-2">
                          {block.content.imageUrl && (
                            <img src={block.content.imageUrl} alt="" className="h-12 w-20 object-cover object-top" />
                          )}
                          <div>
                            <p className="text-[8px] font-bold text-[#111111] font-sans">{block.content.adName}</p>
                            <p className="text-[7px] text-[#999999] font-sans uppercase tracking-wide">Publicidade</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Page Footer */}
            <div className="border-t border-[#E0E0E0] px-6 py-2 flex items-center justify-between mt-auto">
              <p className="text-[7px] text-[#999999] font-sans">Jardel City Post · www.jardelcitypost.com.br</p>
              <p className="text-[7px] text-[#999999] font-sans">Jornalista Renato Silva — MTB 32945</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
